import httpClient from '@/api/httpClient';
import { API_CONFIG } from '@/config/api.config';

export interface FinancialOrder {
  appointmentNumber: string; // Appointment number from appointment table
  createdDate: string; // Created date
  patientName: string; // Patient full name
  serviceName: string; // Item name
  grandTotal: number; // Total amount
  paidAmount: number; // Paid amount
  pendingAmount: number; // Grand total - paid amount
  financialStatus: string; // Financial status
  doctorId: string;
  doctorName?: string;
}

export interface FinancialReportParams {
  fromDate?: string; // yyyy-MM-dd
  toDate?: string;   // yyyy-MM-dd
  status?: string;
  doctorId?: string;
  patientId?: string;
}

export interface FinancialOrderFilters {
  fromDate?: string; // yyyy-MM-dd
  toDate?: string;   // yyyy-MM-dd
  status?: string;
  doctorId?: string;
  patientId?: string;
  itemId?: string;
}

export interface PatientStatementSummary {
  patientId: string;
  patientName: string;
  totalAppointments: number;
  totalOrders: number;
  totalOrderValue: number;
  totalPaid: number;
  totalPending: number;
}

export const ReportApi = {
  async getFinancialOrders(params: FinancialReportParams): Promise<FinancialOrder[]> {
    const { fromDate, toDate } = params;
    try {
      const response = await httpClient.get(API_CONFIG.getUrl(API_CONFIG.ENDPOINTS.REPORT), {
        params: {
          from: fromDate,
          to: toDate,
        },
      });
      if (process.env.NODE_ENV !== 'production') {
        // Only log in development
        console.log('[ReportApi] getFinancialOrders response:', response.data);
      }
      return response.data;
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('[ReportApi] getFinancialOrders error:', error);
      }
      throw new Error('Failed to fetch financial orders. Please try again.');
    }
  },
  async getFinancialOrdersAdvanced(params: FinancialOrderFilters): Promise<FinancialOrder[]> {
    // Use URLSearchParams for robust query param handling
    const queryParams = new URLSearchParams();
    if (params.fromDate) queryParams.append('from', params.fromDate);
    if (params.toDate) queryParams.append('to', params.toDate);
    if (params.status && params.status !== 'all') queryParams.append('status', params.status);
    if (params.doctorId && params.doctorId !== 'all') queryParams.append('doctorId', params.doctorId);
    if (params.patientId) queryParams.append('patientId', params.patientId);
    const url = API_CONFIG.getUrl(API_CONFIG.ENDPOINTS.REPORT_ADVANCED) + `?${queryParams.toString()}`;
    try {
      const response = await httpClient.get(url);
      if (process.env.NODE_ENV !== 'production') {
        console.log('[ReportApi] getFinancialOrdersAdvanced response:', response.data);
      }
      return response.data;
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('[ReportApi] getFinancialOrdersAdvanced error:', error);
      }
      throw new Error('Failed to fetch advanced financial orders. Please try again.');
    }
  },
  async getPatientStatementReport(params: { fromDate: string, toDate: string, patientId?: string }): Promise<PatientStatementSummary[]> {
    const { fromDate, toDate, patientId } = params;
    const queryParams = new URLSearchParams();
    if (fromDate) queryParams.append('from', fromDate);
    if (toDate) queryParams.append('to', toDate);
    if (patientId) queryParams.append('patientId', patientId);
    const url = API_CONFIG.getUrl(API_CONFIG.ENDPOINTS.REPORT + '/patient-statement') + `?${queryParams.toString()}`;
    try {
      const response = await httpClient.get(url);
      if (process.env.NODE_ENV !== 'production') {
        console.log('[ReportApi] getPatientStatementReport response:', response.data);
      }
      return response.data;
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('[ReportApi] getPatientStatementReport error:', error);
      }
      throw new Error('Failed to fetch patient statement report. Please try again.');
    }
  }
};
